import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, MessageCircle, ChevronRight, Instagram, Linkedin, Youtube, Facebook } from 'lucide-react';

const linkClass = "text-slate-400 hover:text-white transition-colors text-sm inline-flex items-center group";

const Footer = () => {
  return (
    <footer className="bg-slate-900 text-white relative overflow-hidden">
      <div className="absolute left-0 top-0 h-1 w-full bg-red-600"></div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-14">
          {/* Company Info */}
          <div>
            <div className="mb-6">
              <img
                src="/images/h0a3ygjr_white-logo.png"
                alt="Bhartiya Vehicles & Engineering Co. Ltd."
                className="h-20 w-auto object-contain"
              />
            </div>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              Leading force in fire and safety engineering with 100 years of excellence.
            </p>
            <div className="flex flex-col gap-1.5 text-xs text-slate-500 uppercase tracking-wider font-medium">
              <span>CIN: U29999DL1982PLC013041</span>
              <span>ISO 9001:2015 Certified</span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-display font-semibold text-sm uppercase tracking-widest mb-5 text-white">Quick Links</h3>
            <ul className="space-y-3">
              <li><Link to="/" className={linkClass}>Home</Link></li>
              <li><Link to="/about" className={linkClass}>About Us</Link></li>
              <li><Link to="/products" className={linkClass}>Products</Link></li>
              <li><Link to="/solutions" className={linkClass}>Solutions</Link></li>
              <li><Link to="/mro" className={linkClass}>MRO</Link></li>
              <li><Link to="/contact" className={linkClass}>Contact</Link></li>
              <li><Link to="/news-media" className={linkClass}>News & Media</Link></li>
            </ul>
          </div>

          {/* Products */}
          <div>
            <h3 className="font-display font-semibold text-sm uppercase tracking-widest mb-5 text-white">Products</h3>
            <ul className="space-y-3 text-sm">
              <li><Link to="/products?category=ARFFV" className={linkClass}><ChevronRight className="h-3 w-3 mr-1 text-red-500" />Firefighting Vehicles</Link></li>
              <li><Link to="/products?category=Firefighting+Equipment" className={linkClass}><ChevronRight className="h-3 w-3 mr-1 text-red-500" />Equipment</Link></li>
              <li><Link to="/products?category=Fire+Protection+Systems" className={linkClass}><ChevronRight className="h-3 w-3 mr-1 text-red-500" />Fire Protection Systems</Link></li>
              <li><Link to="/products?category=Firefighting+Robots" className={linkClass}><ChevronRight className="h-3 w-3 mr-1 text-red-500" />Firefighting Robots</Link></li>
              <li><Link to="/products?category=Rescue+Tools" className={linkClass}><ChevronRight className="h-3 w-3 mr-1 text-red-500" />Rescue Tools</Link></li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-display font-semibold text-sm uppercase tracking-widest mb-5 text-white">Contact Us</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3 text-sm text-slate-400 leading-relaxed">
                <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0 text-red-500" />
                <span>A-29, Industrial Area, Site-IV, Sahibabad, Dist.-GZB (U.P.) 201010</span>
              </li>
              <li className="flex items-center gap-3 text-sm">
                <MessageCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
                <a
                  href="https://wa.me/919205262380"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  +91 92052 62380
                </a>
              </li>
              <li className="flex items-center gap-3 text-sm">
                <Phone className="h-4 w-4 text-red-500 flex-shrink-0" />
                <a href="tel:+911202895295" className="text-slate-400 hover:text-white transition-colors">
                  +91-120-2895295
                </a>
              </li>
              <li className="flex items-center gap-3 text-sm">
                <Mail className="h-4 w-4 text-red-500 flex-shrink-0" />
                <a href="mailto:salesservice@bhartiyavehicles.com" className="text-slate-400 hover:text-white transition-colors whitespace-nowrap">
                  salesservice@bhartiyavehicles.com
                </a>
              </li>
              <li className="flex items-center gap-4 pt-1">
                <a
                  href="https://www.instagram.com/bhartiya_vehicles/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  data-testid="social-instagram-link"
                  className="text-slate-400 hover:text-red-500 transition-colors"
                >
                  <Instagram className="h-5 w-5" />
                </a>
                <a
                  href="https://www.linkedin.com/company/bhartiya-vehicles-&-engineering-company-limited/?viewAsMember=true"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn"
                  data-testid="social-linkedin-link"
                  className="text-slate-400 hover:text-red-500 transition-colors"
                >
                  <Linkedin className="h-5 w-5" />
                </a>
                <a
                  href="https://www.youtube.com/@bhartiyavehicles-w4u"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="YouTube"
                  data-testid="social-youtube-link"
                  className="text-slate-400 hover:text-red-500 transition-colors"
                >
                  <Youtube className="h-5 w-5" />
                </a>
                <a
                  href="https://www.facebook.com/bhartiyagroup"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                  data-testid="social-facebook-link"
                  className="text-slate-400 hover:text-red-500 transition-colors"
                >
                  <Facebook className="h-5 w-5" />
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-800 mt-14 pt-8 flex flex-col md:flex-row justify-between gap-3 text-xs text-slate-500">
          <p>&copy; {new Date().getFullYear()} Bhartiya Vehicles & Engineering Co. Ltd. All rights reserved.</p>
          <p className="uppercase tracking-widest font-medium">Committed to Make in India · Protecting Lives & Infrastructure</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
